import React, { Suspense, lazy, useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { creatorPagePath, isHandleSegment } from "@/lib/creator";
// Eager: `/` must paint the landing page without waiting on a chunk.
// RootPage owns the landing/workspace decision and lazy-loads the workspace.
import RootPage from "./pages/RootPage";
import LandingPage from "./pages/LandingPage";
import NotFound from "./pages/NotFound";
import { Icon } from "@/components/Icon";
import { track } from "@/lib/analytics";
import { recordReferral, referralFromSearch } from "@/lib/referral";
// Eager like PageViewTracker: the consent gate must exist before any
// per-visitor analytics could run (backend#411), so it is not a lazy chunk.
import ConsentBanner from "@/components/ConsentBanner";
// Renders nothing and loads nothing until the env switch is armed AND consent
// is granted, on the surfaces the banner covers (backend#412).
import VisitorAnalytics from "@/components/VisitorAnalytics";

const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const SupportPage = lazy(() => import("./pages/SupportPage"));
const SharedBriefPage = lazy(() => import("./pages/SharedBriefPage"));
// No `CreatorProfilePage` here: it has had no route of its own since #237. It
// is the fall-through inside SharedBriefPage — reached when a handle has no
// pinned bio clip — and is lazily imported from there, so it stays a separate
// chunk and a visitor who only opens clip pages never downloads it.
const LiveClipPage = lazy(() => import("./pages/LiveClipPage"));
const SpotlightPage = lazy(() => import("./pages/SpotlightPage"));
const HowItWorksPage = lazy(() => import("./pages/HowItWorksPage"));
const ClipLandingPage = lazy(() => import("./pages/ClipLandingPage"));
/**
 * Auth lives ONLY inside this lazy chunk (BK-199-4 —
 * docs/decisions/be199-web-editor-surface.md). The public tree below stays
 * provider-free; visitors who never open /edit never download the auth code.
 */
const ClipEditorPage = lazy(() => import("./components/editor/ClipEditorPage"));
const InvitePage = lazy(() => import("./pages/InvitePage"));
const ThreadPage = lazy(() => import("./pages/ThreadPage"));
const ClipStatsPage = lazy(() => import("./pages/ClipStatsPage"));
/**
 * Registered on behalf of WEB-A per ../manticscore-swarm-unfurl/HANDOFF-App.tsx.md.
 * Both pages are prerendered by api/trending.ts and api/wrapped.ts; these routes
 * are the interactive layer that hydrates over that markup, so without them a
 * crawlable page renders blank for humans. The files land with WEB-A's branch.
 */
const TrendingPage = lazy(() => import("./pages/TrendingPage"));
const FeedPage = lazy(() => import("./pages/FeedPage"));
const WrappedPage = lazy(() => import("./pages/WrappedPage"));
const InternalPreviewsPage = lazy(() => import("./pages/InternalPreviewsPage"));
const HandoffPage = lazy(() => import("./pages/HandoffPage"));
const OwnerThreadPage = lazy(() => import("./pages/OwnerThreadPage"));
const OwnerThreadEditorPage = lazy(() => import("./pages/OwnerThreadEditorPage"));
const OwnerPublishedPage = lazy(() => import("./pages/OwnerPublishedPage"));
const OwnerInboxPage = lazy(() => import("./pages/OwnerInboxPage"));
const OwnerSignalsPage = lazy(() => import("./pages/OwnerSignalsPage"));
const OwnerAnalyticsPage = lazy(() => import("./pages/OwnerAnalyticsPage"));
const OwnerSettingsPage = lazy(() => import("./pages/OwnerSettingsPage"));
const OwnerGraphPage = lazy(() => import("./pages/OwnerGraphPage"));
const OwnerDiscoverPage = lazy(() => import("./pages/OwnerDiscoverPage"));

/**
 * Tracks page_viewed on every route change, and attributes a `?ref=` code from
 * any entry point (VW-G1) — an invite link is often shared as
 * `manticscore.com/?ref=CODE`, not only as `/invite/CODE`.
 * Placed inside BrowserRouter.
 */
function PageViewTracker() {
  const location = useLocation();
  const initialReferrer = useRef(document.referrer);

  useEffect(() => {
    const code = referralFromSearch(location.search);
    if (code) recordReferral(code, "ref_param");
  }, [location.search]);

  useEffect(() => {
    track('page_viewed', {
      path: location.pathname,
      referrer: initialReferrer.current,
    });
  }, [location.pathname]);

  return null;
}

/**
 * Scrolls to `location.hash` after the target actually exists.
 *
 * The browser resolves a hash once, at parse time, and this is a lazy-loaded
 * SPA: on a cold load of `/#try-it` the section is 1.3s and one dynamic import
 * away, so the browser looks for `#try-it`, finds nothing, and stays at the
 * top — silently. Clicking the same link from the landing page works, because
 * by then the element is mounted, which is what makes this easy to miss.
 *
 * Six links across five surfaces point at `/#try-it` from another route
 * (`InvitePage` twice, `SpotlightPage`, `ThreadPage`, both footers), and every
 * one of them was landing users at the top of the page.
 *
 * Retries on a frame loop rather than a fixed timeout because the wait is a
 * lazy chunk resolving, not a known duration, and gives up after ~3s so a
 * stale hash cannot scroll the page long after the reader started reading.
 *
 * `behavior: "instant"` is deliberate: `src/index.css` sets `scroll-behavior:
 * smooth`, and a 1,300px glide is exactly what `prefers-reduced-motion` exists
 * to stop (#253). Arriving already-there is not motion.
 */
export function HashScroll() {
  const { hash, key } = useLocation();

  useEffect(() => {
    const id = hash.slice(1);
    if (!id) return;

    let raf = 0;
    const deadline = performance.now() + 3000;

    const tryScroll = () => {
      const target = document.getElementById(id);
      if (target) {
        target.scrollIntoView({ behavior: "instant", block: "start" });
        return;
      }
      if (performance.now() < deadline) raf = requestAnimationFrame(tryScroll);
    };

    raf = requestAnimationFrame(tryScroll);
    return () => cancelAnimationFrame(raf);
    // `key` changes on every navigation, so clicking the same hash twice
    // re-runs this instead of being deduped by an unchanged `hash`.
  }, [hash, key]);

  return null;
}

/**
 * `/clip/:slug` addresses two different things, and react-router cannot tell
 * them apart on its own — it has no regex in paths, so `/clip/bair` and
 * `/clip/s-1a2b3c4d` are the same route to it. This component is the
 * discriminator. #237.
 *
 * **Do not "simplify" this into two competing routes.** Declaring
 * `/clip/:handle` next to `/clip/:slug` does not work: react-router ranks by
 * path *shape*, the two shapes are identical, and the first declared one wins
 * every request. One route with a dispatcher is the only form that expresses
 * "same path, different meaning by shape".
 *
 * The shape test is `isHandleSegment`, and it is exact rather than heuristic:
 * every minted clip slug contains a hyphen, and a handle can never contain
 * one. The same test is applied a request earlier at the edge by the
 * `/clip/:handle(...)` rewrite in `vercel.json`, which is what decides whether
 * a crawler gets creator OG or clip OG. Both spellings of the pattern are
 * pinned to `HANDLE_RE` by `src/lib/creator-routing.test.ts`.
 *
 * ── What a handle resolves against (two stores, not one) ──
 *
 * `SharedBriefPage` reads the bio clip first and falls through to the profile,
 * and those are two different endpoints over two different tables that the
 * backend does not dual-write:
 *
 *   1. `GET /u/{handle}` — the pinned bio clip. Resolves against
 *      `mc_user_profiles.handle` / `mc_user_profiles.bio_session_id`, which
 *      `PATCH /v1/profile` writes (backend#312). Both columns are NULL on
 *      every account in production today, so this read misses for everyone.
 *   2. `GET /v1/u/{handle}` — the profile index. Resolves against
 *      `mc_handles`, the namespace table with the uniqueness, reservation and
 *      release semantics. This is the authoritative handle namespace.
 *
 * `get_own_public_profile` reads `mc_handles` and then overrides it with
 * `mc_user_profiles.handle` when that is set, so the two can name different
 * handles for the same user the moment anyone calls `PATCH /v1/profile`.
 * Nothing here may assume one implies the other: a handle that has a bio clip
 * need not have a profile, and vice versa. Each read stands alone and each
 * miss falls through. Reconciling the stores is backend work.
 */
function ClipPathDispatcher() {
  const { slug = "" } = useParams<{ slug: string }>();
  // Passing `handle` rather than `slug` is also the CMS-collision guard, and
  // it is load-bearing: SharedBriefPage only retries against the CMS clip
  // collection for a `slug`. CMS slugs are human-typed and nothing enforces a
  // hyphen, so an editor typing `bair` would otherwise shadow a creator page.
  // See docs/decisions/237-creator-page-url.md.
  return <SharedBriefPage handle={isHandleSegment(slug) ? slug : undefined} />;
}

/**
 * `/u/:handle` and `/clip/u/:handle` are deprecated by #237 and 308 to
 * `/clip/:handle` at the edge (`vercel.json`). This is the same redirect for
 * in-app navigation, which never reaches the edge.
 */
function DeprecatedHandlePathRedirect() {
  const { handle = "" } = useParams<{ handle: string }>();
  return <Navigate to={creatorPagePath(handle)} replace />;
}

function RouteSuspense({ children }: { children: React.ReactNode }) {
  return (
    <Suspense
      fallback={
        <div className="min-h-screen flex items-center justify-center">
          <Icon name="progress_activity" size="lg" className="animate-spin text-muted-foreground" />
        </div>
      }
    >
      {children}
    </Suspense>
  );
}

class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Application error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column' as const,
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
          padding: '2rem',
          textAlign: 'center' as const,
        }}>
          <h1 style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>Something went wrong</h1>
          <p style={{ color: '#666', marginBottom: '1.5rem' }}>
            The application encountered an unexpected error.
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '0.5rem 1.5rem',
              borderRadius: '0.375rem',
              border: '1px solid #ccc',
              background: '#fff',
              cursor: 'pointer',
              fontSize: '0.875rem',
            }}
          >
            Reload page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

/**
 * No auth/query/toast providers here: the live site (landing + public clip
 * pages) uses plain fetch and no Clerk. Sign-in lives on
 * login.manticscore.com. Do not hoist a provider into this file.
 */
const App = () => (
  <ErrorBoundary>
    <BrowserRouter>
      <PageViewTracker />
      <HashScroll />
      {/* Consent gate for per-visitor analytics on public clip/spotlight/bio
          pages (backend#411). Renders nothing elsewhere or once decided. */}
      <ConsentBanner />
      <VisitorAnalytics />
      <Routes>
              {/* Public routes */}
              {/* `/` is the landing page to a stranger and the workspace to an
                  owner. RootPage decides from a cookie hint and lazy-loads the
                  workspace, so a signed-out visitor's bundle is unchanged. */}
              <Route path="/" element={<RootPage />} />
              <Route path="/overview" element={<LandingPage />} />
              <Route path="/spotlight" element={<RouteSuspense><SpotlightPage /></RouteSuspense>} />
              <Route path="/how-it-works" element={<RouteSuspense><HowItWorksPage /></RouteSuspense>} />
              <Route path="/trending" element={<RouteSuspense><TrendingPage /></RouteSuspense>} />
              {/* Public discovery feed (#122). Server-rendered by api/feed.ts
                  behind the /feed rewrite; this is the hydrating layer. Mode
                  lives in the query string so both orders are linkable. */}
              <Route path="/feed" element={<RouteSuspense><FeedPage /></RouteSuspense>} />
              {/* /discover is rewritten server-side too; this is in-app navigation. */}
              <Route path="/discover" element={<Navigate to="/trending" replace />} />
              <Route path="/wrapped/:slug" element={<RouteSuspense><WrappedPage /></RouteSuspense>} />
              {/* /clips was a duplicate of /spotlight (same data, same filters). */}
              <Route path="/clips" element={<Navigate to="/spotlight" replace />} />
              <Route path="/clip/live/:turnId" element={<RouteSuspense><LiveClipPage /></RouteSuspense>} />
              {/* Digest landing (VW-I1). More specific than /clip/:slug. */}
              <Route path="/clip/:slug/stats" element={<RouteSuspense><ClipStatsPage /></RouteSuspense>} />
              {/* Bare /clip — desktop / failed-UL fallback. Must be before /clip/:slug. */}
              <Route path="/clip" element={<RouteSuspense><ClipLandingPage /></RouteSuspense>} />
              {/* One path, two meanings, resolved by shape — see
                  ClipPathDispatcher. A handle serves the creator page
                  (Clerk-free, OG injected by api/u-og.ts); anything else is a
                  clip slug. */}
              <Route path="/clip/:slug" element={<RouteSuspense><ClipPathDispatcher /></RouteSuspense>} />
              {/* Deprecated by #237. This comment used to warn against
                  pointing `/u/:handle` at CreatorProfilePage, because that
                  would have thrown away the pinned bio clip (backend#312).
                  That warning is superseded, not wrong: `/clip/:handle` serves
                  the bio clip *and* falls through to the profile, so neither
                  page is lost and there is nothing left for these two paths to
                  distinguish. They 308 at the edge; these keep in-app links
                  working. */}
              <Route path="/u/:handle" element={<DeprecatedHandlePathRedirect />} />
              <Route path="/clip/u/:handle" element={<DeprecatedHandlePathRedirect />} />
              <Route path="/invite/:code" element={<RouteSuspense><InvitePage /></RouteSuspense>} />
              <Route path="/thread/:slug" element={<RouteSuspense><ThreadPage /></RouteSuspense>} />
              {/* Authed islands — public tree stays provider-free. */}
              <Route path="/handoff" element={<RouteSuspense><HandoffPage /></RouteSuspense>} />
              <Route path="/inbox" element={<RouteSuspense><OwnerInboxPage /></RouteSuspense>} />
              <Route path="/published" element={<RouteSuspense><OwnerPublishedPage /></RouteSuspense>} />
              <Route path="/signals" element={<RouteSuspense><OwnerSignalsPage /></RouteSuspense>} />
              {/* Authed discovery, at /explore rather than /discover.
                  `/discover` is a Vercel rewrite to the public trending
                  prerender (vercel.json, ca0e610) and has been since long
                  before this surface existed — the edge answers it with
                  server-rendered HTML and the SPA never sees the request, so
                  an owner route there is unreachable in production while
                  looking perfectly fine in `vite dev`. */}
              <Route path="/explore" element={<RouteSuspense><OwnerDiscoverPage /></RouteSuspense>} />
              <Route path="/analytics" element={<RouteSuspense><OwnerAnalyticsPage /></RouteSuspense>} />
              {/* `/settings` was an old web-app path that 404'd after the
                  retirement (#108). It comes back as the owner surface the
                  mock specifies, not as the retired page. */}
              <Route path="/settings" element={<RouteSuspense><OwnerSettingsPage /></RouteSuspense>} />
              <Route path="/graph" element={<RouteSuspense><OwnerGraphPage /></RouteSuspense>} />
              {/* `/t/:id`, not `/thread/:id` — `/thread/:slug` is the public
                  collaborative read view and was here first. */}
              <Route path="/t/:id" element={<RouteSuspense><OwnerThreadPage /></RouteSuspense>} />
              <Route path="/t/:id/components" element={<RouteSuspense><OwnerThreadEditorPage /></RouteSuspense>} />
              {/* `/me` and `/hq` are gone, not redirected. Both are product
                  names this repo already retired (#114), and a redirect keeps
                  a dead name alive in the router and in muscle memory. They
                  404, which is how every other retired route here behaves. */}
              <Route path="/edit/:slug" element={<RouteSuspense><ClipEditorPage /></RouteSuspense>} />
              <Route path="/privacy" element={<RouteSuspense><PrivacyPage /></RouteSuspense>} />
              <Route path="/terms" element={<RouteSuspense><TermsPage /></RouteSuspense>} />
              <Route path="/support" element={<RouteSuspense><SupportPage /></RouteSuspense>} />
              {/* Ops-only Tuist previews (web#88). 404s without token. */}
              <Route path="/internal/previews" element={<RouteSuspense><InternalPreviewsPage /></RouteSuspense>} />

              <Route path="*" element={<NotFound />} />
      </Routes>
    </BrowserRouter>
  </ErrorBoundary>
);

export default App;
